import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FileText, Plus, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { VaccinationFormDialog } from "@/components/applications/ApplicationFormDialog";
import { APPOINTMENT_TYPE_BADGE } from "@/components/patients/appointment-ui-meta";
import { usePetVaccinations } from "@/hooks/usePetApplications";
import type { Database } from "@/integrations/supabase/types";
import { formatDateBR, formatDateTimeBR } from "@/lib/date-utils";
import { getAppointmentTypeIcon } from "@/lib/event-icons";
import { cn } from "@/lib/utils";

type PetVaccinationRow = Database["public"]["Tables"]["pet_applications"]["Row"];

type VaccinationItem = {
  id?: string;
  name?: string | null;
  product_subtype?: { name?: string | null } | null;
} | null;

type VaccinationAppointment = { id: string; starts_at: string | null } | null;
type VaccinationMedicalRecord = { id: string; consultation_date?: string | null } | null;

type VaccinationEntry = PetVaccinationRow & {
  item_id?: string | null;
  next_application_item_id?: string | null;
  has_next_dose?: boolean | null;
  item?: VaccinationItem;
  next_application_item?: VaccinationItem;
  appointment?: VaccinationAppointment;
  medical_record?: VaccinationMedicalRecord;
};

interface ApplicationTabProps {
  petId: string;
  clinicId: string;
  appointmentId?: string;
  medicalRecordId?: string;
  speciesId?: string;
  onVaccinationsChange?: (vaccinations: VaccinationEntry[]) => void;
  onDraftCreatedId?: (id: string) => void;
  onDraftDeletedId?: (id: string) => void;
  variant?: "desktop" | "mobile";
}

const formatApplicationDateTime = (vaccination: VaccinationEntry) =>
  formatDateTimeBR(vaccination.application_date ?? vaccination.created_at) || "-";

const formatDoseValue = (vaccination: VaccinationEntry) =>
  typeof vaccination.dose_number === "number" && Number.isFinite(vaccination.dose_number)
    ? String(vaccination.dose_number)
    : "-";

const formatNextDoseDateTime = (vaccination: VaccinationEntry) => {
  const appointmentStartsAt = vaccination.appointment?.starts_at ?? null;
  const nextDoseDate = appointmentStartsAt || vaccination.next_dose_date;

  if (!nextDoseDate) return "-";
  if (appointmentStartsAt) return formatDateTimeBR(nextDoseDate) || "-";

  return formatDateBR(nextDoseDate) || "-";
};

const getVaccinationSubtypeLabel = (vaccination: VaccinationEntry, t: (key: string) => string) => {
  const subtype = vaccination.item?.product_subtype?.name?.toLowerCase().trim() ?? "vacina";
  return subtype === "medicamento" ? t("vaccines.filters.medicationType") : t("vaccines.filters.vaccineType");
};

function HistorySection({
  vaccinations,
  isLoading,
  onVaccinationClick,
  title,
  description,
  emptyMessage,
  highlightedVaccinationIds,
  t,
}: {
  vaccinations: VaccinationEntry[];
  isLoading: boolean;
  onVaccinationClick: (vaccination: VaccinationEntry) => void;
  title: string;
  description?: string | null;
  emptyMessage: string;
  highlightedVaccinationIds?: Set<string>;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <section className="overflow-hidden rounded-3xl border border-border/60 bg-background">
      <div className="border-b border-border/60 px-5 py-5 sm:px-6">
        <div className="flex items-start gap-4">
          <div className="space-y-1">
            <h3 className="text-xl font-semibold text-foreground">{title}</h3>
            {description ? (
              <p className="text-sm text-muted-foreground">{description}</p>
            ) : null}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3 p-5 sm:p-6">
          <Skeleton className="h-28 w-full rounded-2xl" />
          <Skeleton className="h-28 w-full rounded-2xl" />
        </div>
      ) : vaccinations.length === 0 ? (
        <div className="p-5 text-sm text-muted-foreground sm:p-6">{emptyMessage}</div>
      ) : (
        <div>
          <div className="hidden grid-cols-[140px_140px_60px_140px_140px] gap-4 border-b border-border/60 bg-muted/20 px-5 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground lg:grid">
            <span>{t("vaccines.history.tableHeaders.application")}</span>
            <span>{t("vaccines.history.tableHeaders.appliedAt")}</span>
            <span>{t("vaccines.history.tableHeaders.dose")}</span>
            <span>{t("vaccines.history.tableHeaders.nextDose")}</span>
            <span>{t("vaccines.history.tableHeaders.nextApplication")}</span>
          </div>
          <div className="divide-y divide-border/60 lg:max-h-[400px] lg:overflow-y-auto">
          {vaccinations.map((vaccination) => {
            const isCurrentConsultationApplication = highlightedVaccinationIds?.has(vaccination.id) ?? false;

            return (
              <button
                key={vaccination.id}
                type="button"
                onClick={() => onVaccinationClick(vaccination)}
                className={cn(
                  "w-full px-5 py-4 text-left transition-colors hover:bg-muted/30 sm:px-6",
                  isCurrentConsultationApplication && "bg-primary/5 ring-1 ring-inset ring-primary/25 hover:bg-primary/10",
                )}
              >
              <div className="grid gap-4 lg:grid-cols-[140px_140px_60px_140px_140px] lg:items-start">
                <span
                  className={cn(
                    "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-background/70 shadow-sm ring-1 ring-border/40 lg:hidden",
                    APPOINTMENT_TYPE_BADGE.aplicacao,
                  )}
                >
                  {getAppointmentTypeIcon("aplicacao", { tone: "white", sizeClass: "h-4 w-4" })}
                </span>
                <div className="min-w-0 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 space-y-1">
                      <p className="truncate text-base font-semibold text-foreground lg:text-sm">
                        {vaccination.item?.name ?? t("vaccines.history.defaultItem")}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline" className="w-fit rounded-full">
                          {getVaccinationSubtypeLabel(vaccination, t)}
                        </Badge>
                        {isCurrentConsultationApplication ? (
                          <Badge className="w-fit rounded-full bg-primary text-primary-foreground hover:bg-primary">
                            {t("vaccines.history.currentConsultation")}
                          </Badge>
                        ) : null}
                      </div>
                      {vaccination.medical_record_id ? (
                        <div onClick={(event) => event.stopPropagation()}>
                          <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs">
                            <Link to={`/medical-records/${vaccination.medical_record_id}`}>
                              <FileText className="mr-1 h-3 w-3" />
                              {t("vaccines.history.linkedConsultation")} {formatDateBR(vaccination.medical_record?.consultation_date ?? null)}
                            </Link>
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="space-y-1 lg:space-y-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80 lg:hidden">
                    {t("vaccines.history.tableHeaders.appliedAt")}
                  </p>
                  <p className="text-sm text-foreground">{formatApplicationDateTime(vaccination)}</p>
                </div>

                <div className="space-y-1 lg:space-y-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80 lg:hidden">
                    {t("vaccines.history.tableHeaders.dose")}
                  </p>
                  <p className="text-sm text-foreground">{formatDoseValue(vaccination)}</p>
                </div>

                <div className="space-y-1 lg:space-y-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80 lg:hidden">
                    {t("vaccines.history.tableHeaders.nextDose")}
                  </p>
                  <p className="text-sm text-foreground">
                    {vaccination.next_dose_date && !vaccination.next_dose_completed_at
                      ? formatNextDoseDateTime(vaccination)
                      : "—"}
                  </p>
                </div>

                <div className="space-y-1 lg:space-y-0">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80 lg:hidden">
                    {t("vaccines.history.tableHeaders.nextApplication")}
                  </p>
                  <p className="truncate text-sm text-foreground">
                    {vaccination.next_application_item?.name ?? "—"}
                  </p>
                </div>
              </div>

              {vaccination.batch_number || vaccination.adverse_reaction || vaccination.notes ? (
                <div className="mt-4 space-y-3 border-t border-border/50 pt-4">
                  {vaccination.batch_number ? (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">{t("vaccines.history.batch")}</p>
                      <p className="mt-1 text-sm text-foreground">{vaccination.batch_number}</p>
                    </div>
                  ) : null}

                  {vaccination.adverse_reaction ? (
                    <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-3">
                      <p className="flex items-center gap-2 text-sm font-medium text-destructive">
                        <TriangleAlert className="h-4 w-4" />
                        {t("vaccines.form.adverseReaction")}
                      </p>
                      <p className="mt-2 text-sm text-foreground">{vaccination.adverse_reaction}</p>
                    </div>
                  ) : null}

                  {vaccination.notes ? (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">{t("vaccines.form.notes")}</p>
                      <p className="mt-1 text-sm text-foreground">{vaccination.notes}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
              </button>
            );
          })}
          </div>
        </div>
      )}
    </section>
  );
}

export function ApplicationTab({
  petId,
  clinicId,
  appointmentId,
  medicalRecordId,
  speciesId,
  onVaccinationsChange,
  onDraftCreatedId,
  onDraftDeletedId,
  variant = "desktop",
}: ApplicationTabProps) {
  const { t } = useTranslation(["reports", "patients"]);
  const [pendingVaccinations, setPendingVaccinations] = useState<VaccinationEntry[]>([]);
  const [selectedVaccinationForEdit, setSelectedVaccinationForEdit] = useState<VaccinationEntry | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createApplicationDialogOpen, setCreateApplicationDialogOpen] = useState(false);

  const { data: existingVaccinations, isLoading } = usePetVaccinations(petId);

  const filteredExistingVaccinations = useMemo(() => {
    if (!existingVaccinations || !medicalRecordId) return [];

    return existingVaccinations.filter((vaccination: VaccinationEntry) => vaccination.medical_record_id === medicalRecordId);
  }, [existingVaccinations, medicalRecordId]);

  const allVaccinationsHistory = useMemo(() => {
    if (!existingVaccinations) return [];

    return [...existingVaccinations].sort((a, b) => {
      const timestampA = new Date(a.application_date ?? a.created_at ?? 0).getTime();
      const timestampB = new Date(b.application_date ?? b.created_at ?? 0).getTime();
      return timestampB - timestampA;
    });
  }, [existingVaccinations]);

  const consultationVaccinations = useMemo(() => {
    const combined = medicalRecordId
      ? [
        ...pendingVaccinations.filter((vaccination) => vaccination.medical_record_id === medicalRecordId || !vaccination.medical_record_id),
        ...filteredExistingVaccinations,
      ]
      : pendingVaccinations;

    return combined.sort((a, b) => {
      const timestampA = new Date(a.application_date ?? a.created_at ?? 0).getTime();
      const timestampB = new Date(b.application_date ?? b.created_at ?? 0).getTime();
      return timestampB - timestampA;
    });
  }, [filteredExistingVaccinations, medicalRecordId, pendingVaccinations]);

  const consultationVaccinationIds = useMemo(
    () => new Set(consultationVaccinations.map((vaccination) => vaccination.id)),
    [consultationVaccinations],
  );

  useEffect(() => {
    if (!medicalRecordId) {
      onVaccinationsChange?.(pendingVaccinations);
    }
  }, [medicalRecordId, onVaccinationsChange, pendingVaccinations]);

  useEffect(() => {
    if (medicalRecordId && pendingVaccinations.length > 0) {
      const timer = setTimeout(() => setPendingVaccinations([]), 1000);
      return () => clearTimeout(timer);
    }
  }, [medicalRecordId, pendingVaccinations.length]);

  useEffect(() => {
    if (!editDialogOpen) {
      setSelectedVaccinationForEdit(null);
    }
  }, [editDialogOpen]);

  useEffect(() => {
    if (medicalRecordId) return;
    if (!existingVaccinations || pendingVaccinations.length === 0) return;

    setPendingVaccinations((current) => {
      const indexById = new Map(existingVaccinations.map((entry: VaccinationEntry) => [entry.id, entry]));
      let changed = false;
      const next = current.map((entry) => {
        const updated = indexById.get(entry.id);
        if (!updated || updated === entry) return entry;
        changed = true;
        return updated;
      });
      return changed ? next : current;
    });
  }, [existingVaccinations, medicalRecordId, pendingVaccinations.length]);

  const handleHistoryEdit = (vaccination: VaccinationEntry) => {
    setSelectedVaccinationForEdit(vaccination);
    setEditDialogOpen(true);
  };

  const handleApplicationSaved = (application: VaccinationEntry & { id?: string }) => {
    if (!application.id) return;

    if (!medicalRecordId) {
      onDraftCreatedId?.(application.id);
      setPendingVaccinations((current) => {
        const next = current.filter((entry) => entry.id !== application.id);
        return [application, ...next];
      });
    }
  };

  const handleApplicationDeleted = (applicationId: string) => {
    setPendingVaccinations((current) => current.filter((entry) => entry.id !== applicationId));
    if (!medicalRecordId) {
      onDraftDeletedId?.(applicationId);
    }
  };

  return (
    <div className={variant === "mobile" ? "space-y-4" : "space-y-6"}>
      <section className={cn("border border-border/60 bg-background", variant === "mobile" ? "rounded-2xl bg-card p-4 shadow-sm" : "rounded-3xl p-5 sm:p-6")}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h3 className="text-xl font-semibold text-foreground">{t("reports:vaccines.form.register")}</h3>
            <p className="text-sm text-muted-foreground">{t("reports:vaccines.applicationTab.registerDescription")}</p>
          </div>
          <Button onClick={() => setCreateApplicationDialogOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            {t("reports:vaccines.form.newApplication")}
          </Button>
        </div>
      </section>

      <HistorySection
        vaccinations={allVaccinationsHistory}
        isLoading={isLoading}
        onVaccinationClick={handleHistoryEdit}
        title={t("reports:vaccines.history.title")}
        highlightedVaccinationIds={consultationVaccinationIds}
        emptyMessage={t("reports:vaccines.history.empty")}
        t={t}
      />

      <VaccinationFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        petId={petId}
        clinicId={clinicId}
        appointmentId={appointmentId}
        medicalRecordId={medicalRecordId}
        petSpeciesId={speciesId}
        vaccination={selectedVaccinationForEdit}
        showCreateAppointmentToggle
        onApplicationSaved={handleApplicationSaved}
        onApplicationDeleted={handleApplicationDeleted}
      />

      <VaccinationFormDialog
        open={createApplicationDialogOpen}
        onOpenChange={setCreateApplicationDialogOpen}
        petId={petId}
        clinicId={clinicId}
        appointmentId={appointmentId}
        medicalRecordId={medicalRecordId}
        petSpeciesId={speciesId}
        showCreateAppointmentToggle
        onApplicationSaved={handleApplicationSaved}
        onApplicationDeleted={handleApplicationDeleted}
      />
    </div>
  );
}
